package er.prateek.crossly.game.crossword

/**
 * A tiny seeded generator used for anything the player must see the *same* way
 * twice: which squares come pre-filled, what order the letter rack is in.
 *
 * `kotlin.random.Random(seed)` would do the job, but its sequence is not
 * contractually stable across Kotlin versions or targets — and a rack that
 * reshuffles between a phone and the web build (or between two recompositions
 * after a process restart) reads as a bug. This 64-bit LCG is ours, so the
 * sequence is fixed for good.
 */
internal class PuzzleRandom(seed: Long) {
    private var state: Long = seed xor 0x5DEECE66DL

    private fun next(): Long {
        state = state * 6364136223846793005L + 1442695040888963407L
        return state
    }

    /** Uniform-enough value in `0 until bound`; [bound] must be positive. */
    fun nextInt(bound: Int): Int {
        val bits = (next() ushr 33).toInt() and Int.MAX_VALUE
        return bits % bound
    }

    /** Fisher-Yates, so the same seed always produces the same order. */
    fun <T> shuffled(items: List<T>): List<T> {
        val result = items.toMutableList()
        for (i in result.lastIndex downTo 1) {
            val j = nextInt(i + 1)
            val tmp = result[i]
            result[i] = result[j]
            result[j] = tmp
        }
        return result
    }
}

/**
 * Stable string hash. `String.hashCode()` is not guaranteed to agree across
 * Kotlin targets, and these seeds have to survive a player moving between
 * platforms mid-streak.
 */
internal fun String.stableSeed(): Long = fold(1125899906842597L) { acc, char -> acc * 31 + char.code }
